/**
 * UniServiceIframeModal
 * ─────────────────────
 * Drawer lateral (direita) que embeda o iframe do UniService para:
 *   1. Listar conexões existentes do contrato → user escolhe "Usar esta"
 *      (reaproveita) ou "Reconectar" (cai em FB.login).
 *   2. Botão "Conectar nova com Facebook" sempre disponível.
 *
 * O drawer mantém a página de Connections visível por baixo (slide-in da
 * direita) — comportamento mais leve que o fullscreen anterior.
 *
 * Props:
 *   - open                (boolean)   abre/fecha o drawer
 *   - onClose             (function)  callback ao fechar
 *   - contract            (string?)   contrato (ex: "2166"). Se ausente, o
 *                                     próprio iframe pede ao usuário.
 *   - businessAccountId   (string?)   UUID da conexão pendente. Se vier, o
 *                                     iframe pode usá-la como alvo direto.
 *   - connectionName      (string?)   nome amigável da conexão
 *   - onSuccess           (function?) chamado quando o iframe completa.
 *                                     Recebe { businessAccountId, previousBusinessAccountId,
 *                                     phoneNumber, displayName, reused }.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import api from "../../services/api";

const DRAWER_WIDTH = 460;

const UniServiceIframeModal = ({
  open,
  onClose,
  contract,
  businessAccountId,
  connectionName,
  webhookUrl,
  onSuccess,
}) => {
  const theme = useTheme();
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // A URL do UniService agora vem do BACKEND (UNISERVICE_URL), não mais do .env
  // do frontend. configError = backend respondeu sem a env (500) ou falhou.
  const [uniserviceUrl, setUniserviceUrl] = useState("");
  const [configError, setConfigError] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get("/uniservice-config")
      .then(({ data }) => {
        if (!active) return;
        setUniserviceUrl((data?.url || "").replace(/\/+$/, ""));
        setConfigError(false);
      })
      .catch(() => {
        if (!active) return;
        setUniserviceUrl("");
        setConfigError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const iframeSrc = useMemo(() => {
    if (!uniserviceUrl) return "";
    const params = new URLSearchParams();
    if (contract) params.set("companyId", String(contract));
    if (businessAccountId) params.set("businessAccountId", String(businessAccountId));
    if (connectionName) params.set("name", String(connectionName));
    // webhookUrl deste UniChat (domínio ou ip:porta) — o iframe usa pra criar a
    // webhook subscription no UniService direto do navegador.
    if (webhookUrl) params.set("webhookUrl", String(webhookUrl));
    return `${uniserviceUrl}/iframe/fb-login?${params.toString()}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, businessAccountId, connectionName, webhookUrl, reloadKey, uniserviceUrl]);

  useEffect(() => {
    if (open) setLoading(true);
  }, [open, iframeSrc]);

  // Listener defensivo: só ouvimos mensagens da origem do UniService.
  // Eventos esperados (emitidos pela página /iframe/fb-login):
  //   - uniservice:fb-login-ready     → iframe carregou e SDK pronto
  //   - uniservice:fb-login-complete  → conexão ativada/selecionada; close
  //   - uniservice:fb-login-error     → erro durante FB.login
  //   - uniservice:close              → user clicou em "Fechar"
  useEffect(() => {
    const handler = (event) => {
      try {
        if (!uniserviceUrl) return;
        if (!event.origin || !uniserviceUrl.startsWith(event.origin)) return;
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (!data?.type) return;

        if (data.type === "uniservice:fb-login-complete") {
          // `previousBusinessAccountId` chega quando o servidor mergeou a
          // pending numa row existente OU quando o user reaproveitou uma
          // conexão existente da lista. Em ambos os casos o id remoto pode
          // ter mudado e o parent precisa sincronizar a row local.
          onSuccess?.({
            businessAccountId: data.businessAccountId,
            previousBusinessAccountId: data.previousBusinessAccountId,
            phoneNumber: data.phoneNumber,
            displayName: data.displayName,
            reused: !!data.reused,
            // subscriptionId/secret chegam quando o PRÓPRIO iframe criou a
            // webhook subscription no UniService (browser-side). O parent grava
            // direto, sem o backend precisar alcançar o UniService.
            subscriptionId: data.subscriptionId,
            secret: data.secret,
            // Estado da conta conhecido pelo iframe — o parent grava o status
            // localmente de forma otimista (não espera o backend sincronizar
            // com o UniService, que pode não estar alcançável).
            signupState: data.signupState,
            isActive: data.isActive,
            wabaId: data.wabaId,
          });
          setTimeout(() => onClose?.(), 1200);
        } else if (data.type === "uniservice:close") {
          onClose?.();
        }
      } catch {
        /* ignore non-JSON or foreign messages */
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onClose, onSuccess, uniserviceUrl]);

  const handleReload = () => {
    setLoading(true);
    setReloadKey((k) => k + 1);
  };

  const handleOpenExternal = () => {
    if (iframeSrc) window.open(iframeSrc, "_blank", "noopener,noreferrer");
  };

  const noUrlConfigured = configError;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: DRAWER_WIDTH },
          maxWidth: "100vw",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 1.25,
            bgcolor: theme.palette.success.main + "22",
            color: theme.palette.success.main,
          }}
        >
          <WhatsAppIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
            noWrap
          >
            Conexão WhatsApp Oficial
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
            noWrap
          >
            {contract ? `Contrato ${contract}` : "via UniService"}
          </Typography>
        </Box>
        <Tooltip title="Recarregar">
          <IconButton onClick={handleReload} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Abrir em nova aba">
          <IconButton onClick={handleOpenExternal} size="small" disabled={!iframeSrc}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fechar">
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, position: "relative", bgcolor: "background.default" }}>
        {loading && iframeSrc && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1.5,
              zIndex: 1,
              bgcolor: "background.default",
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">
              Carregando UniService…
            </Typography>
          </Box>
        )}

        {iframeSrc && (
          <iframe
            key={reloadKey}
            ref={iframeRef}
            src={iframeSrc}
            title="UniService"
            onLoad={() => setLoading(false)}
            allow="clipboard-write; geolocation"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              background: "transparent",
              display: "block",
            }}
          />
        )}

        {!iframeSrc && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              color={noUrlConfigured ? "error" : "text.secondary"}
            >
              {noUrlConfigured
                ? "UNISERVICE_URL não configurada no backend. Defina no .env do backend."
                : "Configure o contrato no formulário da conexão."}
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default UniServiceIframeModal;
